import { useTranslation } from 'next-i18next'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import styled from 'styled-components'
import { DepositAnswer } from '@fe/common/api/models/DepositAnswer'
import { Amount } from '@fe/common/components/Amount'
import { Button } from '@fe/common/components/Button'
import { DropdownOptions } from '@fe/common/components/inputs/CustomSelect'
import { Dropdown } from '@fe/common/components/inputs/Dropdown'
import { ErrorText } from '@fe/common/components/typography/ErrorText'
import {
  COLOR,
  FONT_SIZE,
  FONT_WEIGHT,
  SCREEN,
} from '@fe/common/constants/main'
import { useCryptoQr } from '@fe/common/hooks/useCryptoQr'
import { ClipboardCopy } from '@fe/common/src/components/ClipboardCopy'
import { ReceiveData } from '..'
import { requisitesErrorText } from './requisitesErrorText'
import { DepositsApi } from 'src/api/DepositsApi'
import { AccountCurrencyWithDetails } from 'src/types/AccountCurrencyWithDetails'

const NewAddressButton = styled(Button)`
  height: 3.5rem;

  ${SCREEN.ABOVE_MOBILE} {
    height: 6rem;
    text-align: center;
  }
`

const AddressContainer = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;

  > :last-child {
    width: 100%;
    margin-top: 2rem;
  }

  ${SCREEN.ABOVE_MOBILE} {
    flex-direction: row;

    > :first-child {
      margin-right: 2rem;
    }

    > :last-child {
      width: fit-content;
      margin-top: 0;
    }
  }
`

const DetailsContainer = styled.div`
  display: flex;
  margin-top: 4em;
`

const QrCode = styled.img`
  display: none;
  width: 15rem;
  height: 15rem;
  margin-right: 4rem;

  ${SCREEN.ABOVE_MOBILE} {
    display: block;
  }
`

const Rows = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  flex-grow: 1;
  padding: 1rem 0;
`

const Row = styled.div`
  font-weight: ${FONT_WEIGHT.SEMIBOLD};
  overflow-x: auto;

  span {
    display: block;
    white-space: pre;

    :first-child {
      margin-bottom: 1rem;
      color: ${COLOR.GREY};
      font-size: ${FONT_SIZE.SMALL};
    }
  }

  & + & {
    margin-top: 2rem;
  }
`

// The memo / tag belongs to the one address the answer names in `account`.
type MemoSource = Pick<DepositAnswer, 'account' | 'reference'>

interface CryptoDetailsProps {
  currentCurrencyDetails: AccountCurrencyWithDetails
}

export const CryptoDetails: React.FC<CryptoDetailsProps> = ({
  currentCurrencyDetails,
}) => {
  const { t } = useTranslation('transactions')
  const [addresses, setAddresses] = useState<DropdownOptions>([])
  const [memo, setMemo] = useState<MemoSource>()
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string>()
  // The currency can change, or a new address be asked for, while a list
  // loads. Only the latest fetch may set state.
  const lastRequest = useRef(0)

  const { setValue, watch, control } = useFormContext<ReceiveData>()

  const amountIn = watch('amount_in')
  const crypto_address = watch('requestTypeFields.crypto_address') as string

  // The memo the memo row shows: the QR cannot carry it, so it gets no QR.
  const shownMemo =
    memo?.reference && crypto_address === memo.account ? memo.reference : null

  const qrCodeSrc = useCryptoQr(
    crypto_address,
    amountIn,
    currentCurrencyDetails?.code,
    shownMemo
  )

  const fetchAddresses = useCallback(
    async (cryptoSettingsId: number, isNew = false) => {
      const request = ++lastRequest.current
      const isLatest = () => request === lastRequest.current

      // A new address for the same currency keeps the current list until it
      // arrives: if it is refused, the address the user has stays valid.
      if (!isNew) {
        setAddresses([])
        setMemo(undefined)
        setValue('requestTypeFields.crypto_address', null)
      }
      setError(undefined)

      try {
        setIsLoading(true)

        const {
          data: { addresses, account, reference },
        } = await DepositsApi.getCryptoDetails(cryptoSettingsId, isNew)

        if (!isLatest()) {
          return
        }

        const addressOptions = (addresses ?? []).map(address => ({
          value: address,
          label: address,
        }))

        setAddresses(addressOptions)
        setMemo({ account, reference })
        // An empty list is not an error: no address yet, nothing to show.
        setValue(
          'requestTypeFields.crypto_address',
          addressOptions[0]?.value ?? null
        )
      } catch (error) {
        if (isLatest()) {
          setError(requisitesErrorText(error, t))
        }
      } finally {
        if (isLatest()) {
          setIsLoading(false)
        }
      }
    },
    []
  )

  useEffect(() => {
    fetchAddresses(currentCurrencyDetails?.id)
  }, [currentCurrencyDetails?.id])

  // The modal unmounts this while "send as" is on, but the form stays. An
  // answer that comes after the unmount must not set the address of the
  // currency chosen since.
  useEffect(
    () => () => {
      lastRequest.current++
    },
    []
  )

  return (
    <>
      <AddressContainer>
        <Dropdown
          name="requestTypeFields.crypto_address"
          label={t('address')}
          options={addresses}
          control={control}
          isLoading={isLoading}
          isFullWidth
        />

        <NewAddressButton
          onClick={() => fetchAddresses(currentCurrencyDetails?.id, true)}
          isDisabled={isLoading}
        >
          {t('get-new-address')}
        </NewAddressButton>
      </AddressContainer>

      {error && <ErrorText>{error}</ErrorText>}

      <DetailsContainer>
        {/* No code where a wallet would not read it for sure. */}
        {qrCodeSrc && <QrCode src={qrCodeSrc} alt="QR Code" />}

        <Rows>
          <Row>
            <span>{t('address')}</span>
            <ClipboardCopy text={crypto_address}>
              {crypto_address || '-'}
            </ClipboardCopy>
          </Row>

          {shownMemo && (
            <Row>
              <span>{t('memo-tag')}</span>
              <ClipboardCopy text={shownMemo}>{shownMemo}</ClipboardCopy>
            </Row>
          )}

          <Row>
            <span>{t('amount')}</span>
            {amountIn ? (
              <Amount
                value={amountIn}
                decimals={currentCurrencyDetails?.precision}
                code={currentCurrencyDetails?.code}
              />
            ) : (
              '-'
            )}
          </Row>
        </Rows>
      </DetailsContainer>
    </>
  )
}
